/**
 * Derivations shared by the inbound-file review surface — the mini-map strip,
 * the grid, the hover card and the remap inspector all read the same column
 * model so they can never disagree about what a column maps to.
 *
 * Transform/dataType synthesis here mirrors SpecValidator's rules exactly: a
 * spec this file builds must survive PATCH validation without a round trip.
 */

import type {
  InboundFileBinding,
  InboundFileColumnProfile,
  InboundFileProfile,
  InboundFileSpec,
} from '../../services/integrations-api';

export type MappingFamily = 'time' | 'measure' | 'dimension' | 'reference' | 'note' | 'skipped';

/** Mirrors ShapeFingerprint.normalizeHeader — columns match by name, never index. */
export function normalizeHeader(header: string): string {
  return header.normalize('NFC').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Slot → family (spec D8). */
export function slotFamily(slot: string | null | undefined): MappingFamily {
  if (!slot) return 'skipped';
  if (slot === 'transaction_timestamp' || slot.startsWith('date_')) return 'time';
  if (slot.startsWith('measure_')) return 'measure';
  if (slot.startsWith('name_') || slot.startsWith('label_') || slot.startsWith('flag_')) {
    return 'dimension';
  }
  if (slot.startsWith('ref_')) return 'reference';
  if (slot.startsWith('note_')) return 'note';
  return 'skipped';
}

/**
 * Static class strings — Tailwind only sees classes it can read literally, so
 * these can never be assembled as `bg-map-${family}-bg`.
 */
export const FAMILY_STYLE: Record<MappingFamily, { badge: string; column: string; label: string }> = {
  time: {
    badge: 'bg-map-time-bg text-map-time',
    column: 'bg-map-time-bg/50',
    label: 'time',
  },
  measure: {
    badge: 'bg-map-measure-bg text-map-measure',
    column: '',
    label: 'measure',
  },
  dimension: {
    badge: 'bg-map-dimension-bg text-map-dimension',
    column: '',
    label: 'dimension',
  },
  reference: {
    badge: 'bg-map-reference-bg text-map-reference',
    column: '',
    label: 'reference',
  },
  note: {
    badge: 'bg-map-note-bg text-map-note',
    column: '',
    label: 'note',
  },
  skipped: {
    badge: 'border border-dashed border-border text-muted-foreground',
    column: 'bg-muted/40 text-muted-foreground',
    label: 'skipped',
  },
};

export const FAMILY_ORDER: MappingFamily[] = [
  'time', 'measure', 'dimension', 'reference', 'note', 'skipped',
];

/** Mirrors TRANSACTION_SLOTS (backend types) — the closed set the executor writes. */
export const TRANSACTION_SLOTS = [
  'transaction_timestamp',
  'measure_1', 'measure_2', 'measure_3', 'measure_4',
  'measure_5', 'measure_6', 'measure_7', 'measure_8',
  'name_1', 'name_2', 'name_3',
  'label_1', 'label_2', 'label_3', 'label_4', 'label_5',
  'label_6', 'label_7', 'label_8', 'label_9',
  'note_1', 'note_2', 'note_3',
  'ref_1', 'ref_2', 'ref_3', 'ref_4', 'ref_5', 'ref_6', 'ref_7', 'ref_8',
  'date_1', 'date_2', 'date_3',
  'flag_1', 'flag_2', 'flag_3', 'flag_4',
] as const;

export function slotLabel(slot: string): string {
  return slot === 'transaction_timestamp' ? 'timestamp' : slot;
}

// ── Column model ─────────────────────────────────────────────────────────────

export interface ReviewColumn {
  /** File order — also the grid/strip render order and the scroll anchor id. */
  index: number;
  header: string;
  key: string;
  binding: InboundFileBinding | null;
  slot: string | null;
  family: MappingFamily;
  isMaster: boolean;
  isTimestamp: boolean;
  profile: InboundFileColumnProfile | null;
  skipReason: string | null;
  worthALook: boolean;
  /** Other headers feeding the same binding (Prénom + Nom → name_1). */
  siblings: string[];
}

export function buildReviewColumns(
  spec: InboundFileSpec,
  profile: InboundFileProfile | null,
): ReviewColumn[] {
  // previewHeaders is the only source that preserves raw casing AND file order;
  // profile.columns is the same order, and the fingerprint the last resort.
  const headers =
    profile?.previewHeaders ??
    profile?.columns.map((c) => c.column) ??
    spec.fingerprint.columns;

  const bindingByColumn = new Map<string, InboundFileBinding>();
  for (const b of spec.bindings) {
    for (const source of b.source) bindingByColumn.set(normalizeHeader(source), b);
  }
  const profileByColumn = new Map(
    (profile?.columns ?? []).map((c) => [normalizeHeader(c.column), c]),
  );
  const skipByColumn = new Map(
    (spec.skippedColumns ?? []).map((s) => [normalizeHeader(s.column), s]),
  );

  return headers.map((header, index) => {
    const key = normalizeHeader(header);
    const binding = bindingByColumn.get(key) ?? null;
    const skip = skipByColumn.get(key) ?? null;
    return {
      index,
      header,
      key,
      binding,
      slot: binding?.slot ?? null,
      family: slotFamily(binding?.slot),
      isMaster: binding?.masterMetric === true,
      isTimestamp: binding?.slot === 'transaction_timestamp',
      profile: profileByColumn.get(key) ?? null,
      skipReason: binding ? null : skip?.reason ?? 'Not selected',
      worthALook: !binding && skip?.worthALook === true,
      siblings: binding ? binding.source.filter((s) => normalizeHeader(s) !== key) : [],
    };
  });
}

/** One-line summary of a transform, for the hover card. */
export function transformSummary(binding: InboundFileBinding | null): string | null {
  const t = binding?.transform;
  if (!t) return null;
  switch (t.op) {
    case 'parse_date':
    case 'parse_datetime':
      return `read as a date (${String(t.format ?? '')})`;
    case 'parse_number':
      return `read as a number${t.decimalSep === ',' ? ' (comma decimals)' : ''}`;
    case 'concat':
      return `joined with "${String(t.separator ?? '')}"`;
    case 'coalesce':
      return 'first non-empty of its columns';
    case 'constant':
      return `fixed value ${String(t.value)}`;
    case 'map_value':
      return 'values translated by a lookup';
    case 'scale':
      return `scaled ×${String(t.factor)}`;
    default:
      return t.op.replace(/_/g, ' ');
  }
}

// ── Before-you-approve flags (spec D6) ───────────────────────────────────────

export interface ReviewFlag {
  key: string;
  kind: 'timezone' | 'worth-a-look' | 'duplicate-keys' | 'parse-failures';
  title: string;
  detail: string;
  /** Column to scroll to when the flag is clicked. */
  columnIndex?: number;
}

export function buildFlags(
  spec: InboundFileSpec,
  profile: InboundFileProfile | null,
  columns: ReviewColumn[],
  /** An approved map is being shown — no dock to ask, no draft to re-propose. */
  readOnly = false,
): ReviewFlag[] {
  const flags: ReviewFlag[] = [];

  // Almost always means the business never set its timezone: UTC-midnight dates
  // group under the previous local day once the oracle localizes them.
  if (spec.timezone === 'UTC') {
    flags.push({
      key: 'timezone',
      kind: 'timezone',
      title: 'Timezone is UTC',
      detail:
        `Dates ${readOnly ? 'are' : 'will be'} read as UTC midnight, so local evening activity can ` +
        'land on the previous day. ' +
        (readOnly
          ? 'The timezone is fixed in this mapping version — set the business timezone and the next version will pick it up.'
          : 'Set the business timezone, then ask Zamino to refresh the mapping.'),
    });
  }

  const failedRows = profile
    ? profile.totalRows - profile.parsedRows - profile.rejectedRows
    : 0;
  if (failedRows > 0) {
    flags.push({
      key: 'parse-failures',
      kind: 'parse-failures',
      title: `${failedRows} row${failedRows === 1 ? '' : 's'} failed to parse`,
      detail:
        profile?.failures[0]?.reason ??
        'These rows will not be imported until the mapping handles them.',
    });
  }

  if (profile && profile.naturalKeyCollisions > 0) {
    flags.push({
      key: 'duplicate-keys',
      kind: 'duplicate-keys',
      title: `${profile.naturalKeyCollisions} duplicate row identities`,
      detail:
        'Rows sharing an identity overwrite each other. Row identity may need another field.',
    });
  }

  for (const column of columns.filter((c) => c.worthALook)) {
    flags.push({
      key: `worth-a-look:${column.key}`,
      kind: 'worth-a-look',
      title: `${column.header} skipped`,
      detail: column.skipReason ?? 'Zamino flagged this skip as a judgment call.',
      columnIndex: column.index,
    });
  }

  return flags;
}

// ── Remap: build the spec a PATCH will accept ────────────────────────────────

/** Unambiguous formats only — a wrong guess at DD/MM vs MM/DD silently corrupts dates. */
const DATE_FORMAT_CANDIDATES = [
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD HH:mm',
  'YYYY-MM-DD',
  'YYYY/MM/DD HH:mm:ss',
  'YYYY/MM/DD HH:mm',
  'YYYY/MM/DD',
];

const FORMAT_TESTS: Record<string, RegExp> = {
  'YYYY-MM-DD HH:mm:ss': /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}:\d{1,2}$/,
  'YYYY-MM-DD HH:mm': /^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{1,2}$/,
  'YYYY-MM-DD': /^\d{4}-\d{1,2}-\d{1,2}$/,
  'YYYY/MM/DD HH:mm:ss': /^\d{4}\/\d{1,2}\/\d{1,2} \d{1,2}:\d{1,2}:\d{1,2}$/,
  'YYYY/MM/DD HH:mm': /^\d{4}\/\d{1,2}\/\d{1,2} \d{1,2}:\d{1,2}$/,
  'YYYY/MM/DD': /^\d{4}\/\d{1,2}\/\d{1,2}$/,
};

export function sniffDateFormat(samples: string[]): string | null {
  if (samples.length === 0) return null;
  return (
    DATE_FORMAT_CANDIDATES.find((format) =>
      samples.every((s) => FORMAT_TESTS[format].test(s.trim())),
    ) ?? null
  );
}

export function sniffNumberDecimalSep(samples: string[]): '.' | ',' | null {
  if (samples.length === 0) return null;
  const trimmed = samples.map((s) => s.trim());
  if (trimmed.every((s) => /^-?\d+(\.\d+)?$/.test(s))) return '.';
  if (trimmed.every((s) => /^-?\d+(,\d+)?$/.test(s))) return ',';
  return null;
}

/**
 * Which slots the inspector may offer for a column. A slot is offered only when
 * we can author a binding the validator will accept without guessing —
 * flag_* needs a case table and ambiguous date formats need a human, so both
 * are left to the discuss dock.
 */
export function offerableSlots(column: ReviewColumn, spec: InboundFileSpec): string[] {
  const taken = new Set(spec.bindings.map((b) => b.slot));
  const samples = column.profile?.sampleValues ?? [];
  const keepsTransform = column.binding !== null;
  const canDate = keepsTransform
    ? column.family === 'time'
    : sniffDateFormat(samples) !== null;
  const canNumber = keepsTransform
    ? column.family === 'measure'
    : sniffNumberDecimalSep(samples) !== null;

  return TRANSACTION_SLOTS.filter((slot) => {
    if (slot === column.slot) return true;
    if (taken.has(slot)) return false;
    const family = slotFamily(slot);
    if (slot.startsWith('flag_')) return false;
    if (family === 'time') return canDate;
    if (family === 'measure') return canNumber;
    return true;
  });
}

/** Which families the select left out, and why — so a missing option is never mute. */
export function unofferedReason(column: ReviewColumn): string | null {
  // A bound column keeps its transform, so it can only move within families
  // that transform already satisfies. Reading it a new way is the dock's job.
  if (column.binding) {
    if (column.family === 'time' || column.family === 'measure') return null;
    return 'Date and number fields need a format Zamino reads from the file — ask below to change how this column is read.';
  }

  const samples = column.profile?.sampleValues ?? [];
  if (samples.length === 0) {
    return 'This column is empty in the file, so only text fields are offered.';
  }
  const missing = [
    sniffDateFormat(samples) === null ? 'date' : null,
    sniffNumberDecimalSep(samples) === null ? 'number' : null,
  ].filter((m): m is string => m !== null);
  if (missing.length === 0) return null;
  const subject = missing.join(' and ');
  return `${subject[0].toUpperCase()}${subject.slice(1)} fields are not offered — ask Zamino below if this column should be one.`;
}

const DATA_TYPE_BY_FAMILY: Record<Exclude<MappingFamily, 'skipped'>, string> = {
  time: 'date',
  measure: 'count',
  dimension: 'category',
  reference: 'reference',
  note: 'text',
};

function slugify(label: string, taken: ReadonlySet<string>): string {
  // NFD splits an accented letter into base + combining mark; dropping the
  // non-ASCII half leaves the base, so Prenom-with-accent slugs as prenom.
  const base =
    label
      .normalize('NFD')
      .replace(/[^\u0020-\u007e]/g, '')
      .toLowerCase()
      .replace(/[^a-z]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '') || 'field';
  let slug = base;
  while (taken.has(slug)) slug = `${slug}_x`; // friendlySlug allows letters + _ only
  return slug;
}

export interface RemapInput {
  column: ReviewColumn;
  /** null = don't import this column. */
  slot: string | null;
  friendlyLabel: string;
  description: string;
  unit: string;
}

/**
 * Apply one inspector edit to the whole spec. naturalKey and rowRules follow a
 * slot MOVE so the result stays valid; the caller blocks unbinding when either
 * still points at the slot (changing row identity is the dock's job).
 */
export function applyRemap(spec: InboundFileSpec, input: RemapInput): InboundFileSpec {
  const { column, slot } = input;
  const previous = column.binding;
  const others = spec.bindings.filter((b) => b !== previous);

  if (slot === null) {
    return {
      ...spec,
      bindings: others,
      skippedColumns: [
        ...(spec.skippedColumns ?? []).filter(
          (s) => normalizeHeader(s.column) !== column.key,
        ),
        { column: column.header, reason: 'Removed during review' },
      ],
    };
  }

  const family = slotFamily(slot) as Exclude<MappingFamily, 'skipped'>;
  const samples = column.profile?.sampleValues ?? [];
  const takenSlugs = new Set(others.map((b) => b.friendlySlug));
  const label = input.friendlyLabel.trim() || column.header;
  const unit = input.unit.trim();

  let transform = previous?.transform;
  if (!previous) {
    if (family === 'time') {
      const format = sniffDateFormat(samples) as string;
      transform = { op: format.includes('HH') ? 'parse_datetime' : 'parse_date', format };
    } else if (family === 'measure') {
      const decimalSep = sniffNumberDecimalSep(samples) as '.' | ',';
      transform = decimalSep === ',' ? { op: 'parse_number', decimalSep } : { op: 'parse_number' };
    }
  }

  const dataType =
    previous && slotFamily(previous.slot) === family
      ? previous.dataType
      : ((family === 'measure' && unit && unit === spec.currency
          ? 'currency'
          : DATA_TYPE_BY_FAMILY[family]) as InboundFileBinding['dataType']);

  const binding: InboundFileBinding = {
    slot,
    source: previous ? previous.source : [column.header],
    friendlySlug: previous?.friendlySlug ?? slugify(label, takenSlugs),
    friendlyLabel: label,
    description: input.description.trim() || label,
    dataType,
    ...(unit ? { unit } : {}),
    ...(previous?.masterMetric ? { masterMetric: true } : {}),
    ...(transform ? { transform } : {}),
  };

  const moved = previous && previous.slot !== slot;
  return {
    ...spec,
    bindings: [...others, binding],
    naturalKey: moved
      ? spec.naturalKey.map((s) => (s === previous.slot ? slot : s))
      : spec.naturalKey,
    ...(moved && spec.rowRules
      ? { rowRules: spec.rowRules.map((r) => (r.slot === previous.slot ? { ...r, slot } : r)) }
      : {}),
    skippedColumns: (spec.skippedColumns ?? []).filter(
      (s) => normalizeHeader(s.column) !== column.key,
    ),
  };
}
